// src/components/BILayout.tsx — Top-navbar layout 2026
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Brain, ArrowLeft } from 'lucide-react';
import ChatWidget from './ChatWidget';
import { useAuth } from '../contexts/AuthContext';

interface BILayoutProps {
  children: React.ReactNode;
  showBackButton?: boolean;
  backUrl?: string;
}

const BILayout: React.FC<BILayoutProps> = ({ children, showBackButton = false, backUrl = '/dashboard' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const scrollToSection = (sectionId: string) => {
    // Если уже на /app/bi — просто скроллим
    if (location.pathname === '/app/bi' || location.pathname === '/app/bi/') {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
    }
    // Иначе — навигируем и после загрузки скроллим
    navigate('/app/bi');
    setTimeout(() => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 300);
  };

  const tabs = [
    { id: 'dashboards', icon: <LayoutDashboard size={16} />, label: 'Дашборды', onClick: () => scrollToSection('bi-dashboards') },
    { id: 'mindmaps', icon: <Brain size={16} />, label: 'Mind-карты', onClick: () => scrollToSection('bi-mindmaps') },
  ];

  const isTabActive = (tab: any) => {
    if (location.pathname !== '/app/bi' && location.pathname !== '/app/bi/') return false;
    return tab.id === 'dashboards';
  };

  const userInitial = (user?.name || user?.email || '?')[0].toUpperCase();

  return (
    <div style={styles.root}>
      {/* Фоновые пятна */}
      <div style={styles.bgBlur1} />
      <div style={styles.bgBlur2} />
      <div style={styles.bgBlur3} />

      {/* ─── TOP NAVBAR ─── */}
      <header style={styles.header}>
        <div style={styles.headerInner}>
          {/* Логотип */}
          <div style={styles.brand} onClick={() => navigate('/dashboard')}>
            <img src="/images/10.png" alt="LAMA" style={styles.brandLogo} />
            <div style={styles.brandText}>
              <div style={styles.brandTitle}>Лама BI</div>
              <div style={styles.brandSub}>Аналитика и дашборды</div>
            </div>
          </div>

          {/* Табы */}
          <nav style={styles.tabs}>
            {tabs.map((tab, idx) => {
              const active = isTabActive(tab);
              return (
                <button
                  key={idx}
                  onClick={tab.onClick}
                  style={{
                    ...styles.tab,
                    ...(active ? styles.tabActive : {}),
                  }}
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = 'rgba(102,126,234,0.1)';
                      e.currentTarget.style.color = '#2575fc';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#5a5f7a';
                    }
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', color: active ? '#fff' : 'inherit' }}>
                    {tab.icon}
                  </span>
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Справа: юзер + назад */}
          <div style={styles.headerRight}>
            <button
              onClick={() => navigate(backUrl)}
              style={styles.backBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(102,126,234,0.15)';
                e.currentTarget.style.transform = 'translateX(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.6)';
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <ArrowLeft size={14} />
              <span>Все модули</span>
            </button>

            {user && (
              <div style={styles.userPill} title={user.name || user.email}>
                {user.photo ? (
                  <img src={user.photo} alt={user.name || 'User'} style={styles.userAvatarImg} />
                ) : (
                  <div style={styles.userAvatar}>{userInitial}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ─── CONTENT ─── */}
      <main style={styles.main}>
        {showBackButton && (
          <button
            onClick={() => navigate(backUrl)}
            style={styles.contentBackBtn}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, #6a11cb 0%, #2575fc 100%)';
              e.currentTarget.style.color = 'white';
              e.currentTarget.style.borderColor = 'transparent';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.7)';
              e.currentTarget.style.color = '#2575fc';
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.6)';
            }}
          >
            <ArrowLeft size={16} />
            <span>Назад</span>
          </button>
        )}
        {children}
      </main>

      <ChatWidget />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  root: {
    minHeight: '100vh',
    background: 'linear-gradient(145deg, #f8fafd 0%, #e8f0fe 50%, #f0e8ff 100%)',
    position: 'relative',
    overflowX: 'clip' as any,
    fontFamily: '"Inter", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
  },

  bgBlur1: { position: 'fixed', top: '-15%', left: '-10%', width: 600, height: 600, background: 'radial-gradient(circle, rgba(106,17,203,0.18) 0%, rgba(106,17,203,0) 70%)', borderRadius: '50%', filter: 'blur(60px)', zIndex: 0, pointerEvents: 'none' },
  bgBlur2: { position: 'fixed', bottom: '-15%', right: '-10%', width: 700, height: 700, background: 'radial-gradient(circle, rgba(37,117,252,0.18) 0%, rgba(37,117,252,0) 70%)', borderRadius: '50%', filter: 'blur(60px)', zIndex: 0, pointerEvents: 'none' },
  bgBlur3: { position: 'fixed', top: '40%', left: '50%', width: 500, height: 500, transform: 'translate(-50%, -50%)', background: 'radial-gradient(circle, rgba(139,92,246,0.10) 0%, rgba(139,92,246,0) 70%)', borderRadius: '50%', filter: 'blur(60px)', zIndex: 0, pointerEvents: 'none' },

  // HEADER
  header: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    padding: '12px 20px',
    pointerEvents: 'none' as any,
  },
  headerInner: {
    maxWidth: 1600,
    margin: '0 auto',
    background: 'rgba(255,255,255,0.65)',
    backdropFilter: 'blur(30px) saturate(180%)',
    WebkitBackdropFilter: 'blur(30px) saturate(180%)',
    border: '1px solid rgba(255,255,255,0.7)',
    borderRadius: 22,
    boxShadow: '0 12px 40px rgba(31,38,135,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
    display: 'flex',
    alignItems: 'center',
    gap: 20,
    padding: '10px 18px',
    pointerEvents: 'auto' as any,
  },

  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    flexShrink: 0,
    paddingRight: 16,
    borderRight: '1px solid rgba(0,0,0,0.06)',
  },
  brandLogo: { height: 36, width: 'auto' },
  brandText: { minWidth: 0 },
  brandTitle: { fontSize: 15, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.15, letterSpacing: -0.3 },
  brandSub: { fontSize: 10, color: '#8b8fa8', fontWeight: 600, marginTop: 1 },

  tabs: { display: 'flex', gap: 4, flex: 1 },
  tab: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 16px',
    borderRadius: 12,
    border: 'none',
    background: 'transparent',
    color: '#5a5f7a',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },
  tabActive: {
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: '#fff',
    boxShadow: '0 8px 20px rgba(102,126,234,0.3)',
  },

  headerRight: { display: 'flex', alignItems: 'center', gap: 10 },

  backBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    borderRadius: 12,
    background: 'rgba(255,255,255,0.6)',
    border: '1px solid rgba(102,126,234,0.15)',
    color: '#5a5f7a',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },

  userPill: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  userAvatar: {
    width: 34,
    height: 34,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 800,
    boxShadow: '0 6px 16px rgba(102,126,234,0.25)',
  },
  userAvatarImg: {
    width: 34,
    height: 34,
    borderRadius: 12,
    objectFit: 'cover',
    border: '2px solid rgba(255,255,255,0.9)',
    boxShadow: '0 6px 16px rgba(102,126,234,0.2)',
  },

  // CONTENT
  main: {
    maxWidth: 1600,
    margin: '0 auto',
    padding: '24px 32px 40px',
    position: 'relative',
    zIndex: 1,
  },
  contentBackBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 18px',
    borderRadius: 40,
    background: 'rgba(255,255,255,0.7)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.6)',
    color: '#2575fc',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 16,
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  },
};

export default BILayout;
